import { PersonaConfig, PersonaOrder, OrderStatus } from "../types";

// Fintech (fintech.com) B2B invoice & payments demo — Claire fields calls from
// restaurant / bar / hotel / grocery finance teams whose chat was deflected to
// phone. The whole point of the picker is the SERVICE TIER: Platinum, Gold and
// Silver accounts ask the same question and get materially different promises.
// The agent reads the account live on the backend; this is the picker + CRM
// context only.
export interface FintechCustomer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  loyaltyTier: string; // service tier — "Platinum" | "Gold" | "Silver"
  crmNotes: string;
  ordersCount: number;
  demoScenario?: string;
  orders: { orderId: string; product: string; status: string; date?: string }[];
}

export const FINTECH_CUSTOMERS: FintechCustomer[] = [
  // ─── Platinum ───────────────────────────────────────────────────────────────
  {
    id: "100248",
    name: "Dana Whitfield",
    firstName: "Dana",
    lastName: "Whitfield",
    email: "dana.whitfield@gmail.com",
    phone: "(813) 555-0142",
    loyaltyTier: "Platinum",
    crmNotes:
      "The Maple Room / Maple and Vine Hospitality Group — 6 restaurants, Tampa FL. Bank returned Tuesday's $38,412.65 draft for insufficient funds, flipping 9 invoices back to unpaid; Southern Glazer's is refusing tomorrow's delivery.",
    ordersCount: 3,
    demoScenario: "returned_payment",
    orders: [
      { orderId: "PAY-5540921", product: "Returned weekly draft — $38,412.65", status: "delayed" },
      { orderId: "INV-8842011", product: "Southern Glazer's — $14,208.40", status: "exception" },
      { orderId: "INV-8842047", product: "Breakthru Beverage — $9,642.85", status: "exception" },
    ],
  },
  {
    id: "100502",
    name: "Kevin O'Donnell",
    firstName: "Kevin",
    lastName: "O'Donnell",
    email: "kevin.odonnell@gmail.com",
    phone: "(228) 555-0110",
    loyaltyTier: "Platinum",
    crmNotes:
      "Gulf Star Resort and Casino — 7 outlets, Biloxi MS. Three days of invoice files have not landed in Sage Intacct and month-end close is Friday.",
    ordersCount: 2,
    demoScenario: "integration_sync_failure",
    orders: [
      { orderId: "INV-8871120", product: "Southern Glazer's — $31,420.75", status: "in_transit" },
      { orderId: "INV-8871188", product: "US Foods — $24,885.10", status: "in_transit" },
    ],
  },
  {
    id: "100788",
    name: "Tom Ashford",
    firstName: "Tom",
    lastName: "Ashford",
    email: "tom.ashford@gmail.com",
    phone: "(303) 555-0189",
    loyaltyTier: "Platinum",
    crmNotes:
      "Summit Hotels — 11 properties, Denver CO. The 12th property opens in 3 weeks and needs 3 distributors connected before it goes live.",
    ordersCount: 1,
    demoScenario: "new_location_activation",
    orders: [
      { orderId: "PAY-5595330", product: "Monday batch, 10 properties — $102,885.40", status: "in_transit" },
    ],
  },
  {
    id: "101036",
    name: "Michelle Trang",
    firstName: "Michelle",
    lastName: "Trang",
    email: "michelle.trang@gmail.com",
    phone: "(619) 555-0166",
    loyaltyTier: "Platinum",
    crmNotes:
      "Crescent Bay Hotels — 5 hotels, San Diego CA. Wants beer split bottle vs draft and spirits broken out per property in the GL coding.",
    ordersCount: 1,
    demoScenario: "gl_coding",
    orders: [
      { orderId: "INV-8904120", product: "Southern Glazer's — $34,118.90", status: "in_transit" },
    ],
  },

  // ─── Gold ───────────────────────────────────────────────────────────────────
  {
    id: "100377",
    name: "Marcus Bell",
    firstName: "Marcus",
    lastName: "Bell",
    email: "marcus.bell@gmail.com",
    phone: "(512) 555-0198",
    loyaltyTier: "Gold",
    crmNotes:
      "Riverside Tap House — bar, Austin TX. Signed for a Reyes beer delivery 4 days ago (about $4,268.40); the invoice never transmitted so it is not in the portal.",
    ordersCount: 2,
    demoScenario: "missing_invoice",
    orders: [
      { orderId: "INV-8850233", product: "Reyes Beverage — $4,268.40 (not in portal)", status: "exception" },
      { orderId: "INV-8850198", product: "Reyes Beverage — $3,914.55", status: "delivered" },
    ],
  },
  {
    id: "100589",
    name: "Sandra Nunez",
    firstName: "Sandra",
    lastName: "Nunez",
    email: "sandra.nunez@gmail.com",
    phone: "(614) 555-0163",
    loyaltyTier: "Gold",
    crmNotes:
      "Hillcrest Grocery — 4 stores, Columbus OH. Wine case price is up about $6 a case versus the last four deliveries, plus unexpected split-case fees.",
    ordersCount: 1,
    demoScenario: "cost_variance",
    orders: [
      { orderId: "INV-8871402", product: "Republic National — $9,486.75 (disputed)", status: "exception" },
    ],
  },
  {
    id: "100701",
    name: "Gina Costa",
    firstName: "Gina",
    lastName: "Costa",
    email: "gina.costa@gmail.com",
    phone: "(201) 555-0154",
    loyaltyTier: "Gold",
    crmNotes:
      "Nonna's Kitchen — 2 restaurants, Hoboken NJ. Switching banks; a $9,118.75 draft sits inside the 2-3 day verification window so it will still hit the old account.",
    ordersCount: 1,
    demoScenario: "bank_account_change",
    orders: [
      { orderId: "PAY-5590441", product: "Scheduled draft — $9,118.75 (old account)", status: "in_transit" },
    ],
  },
  {
    id: "100912",
    name: "Ryan Voss",
    firstName: "Ryan",
    lastName: "Voss",
    email: "ryan.voss@gmail.com",
    phone: "(616) 555-0147",
    loyaltyTier: "Gold",
    crmNotes:
      "Copper Kettle Taproom — 2 sites, Grand Rapids MI. Cash is tight before a festival weekend; wants the exact draft date and to pay one distributor early.",
    ordersCount: 1,
    demoScenario: "draft_timing",
    orders: [
      { orderId: "PAY-5601887", product: "Weekly draft — $5,240.60", status: "in_transit" },
    ],
  },

  // ─── Silver ─────────────────────────────────────────────────────────────────
  {
    id: "100415",
    name: "Priya Raman",
    firstName: "Priya",
    lastName: "Raman",
    email: "priya.raman@gmail.com",
    phone: "(912) 555-0177",
    loyaltyTier: "Silver",
    crmNotes:
      "Quick Stop Market — 3 stores, Savannah GA. Two identical $12,304.18 ACH drafts hit the account on the same day.",
    ordersCount: 2,
    demoScenario: "duplicate_draft",
    orders: [
      { orderId: "PAY-5561203", product: "ACH batch — $12,304.18", status: "delivered" },
      { orderId: "PAY-5561288", product: "Duplicate ACH batch — $12,304.18", status: "delayed" },
    ],
  },
  {
    id: "100634",
    name: "Andre Beaumont",
    firstName: "Andre",
    lastName: "Beaumont",
    email: "andre.beaumont@gmail.com",
    phone: "(504) 555-0121",
    loyaltyTier: "Silver",
    crmNotes:
      "Bayou Bottle Shop — liquor store, New Orleans LA. A $6,842.30 draft returned 9 days ago for uncollected funds and never cleared; the account is now on the Louisiana state delinquent list and Republic refused this morning's delivery.",
    ordersCount: 2,
    demoScenario: "alcohol_compliance_hold",
    orders: [
      { orderId: "PAY-5578110", product: "Returned draft — $6,842.30", status: "delayed" },
      { orderId: "INV-8880115", product: "Republic National — $4,218.90 (past credit window)", status: "exception" },
    ],
  },
  {
    id: "100845",
    name: "Lena Park",
    firstName: "Lena",
    lastName: "Park",
    email: "lena.park@gmail.com",
    phone: "(206) 555-0132",
    loyaltyTier: "Silver",
    crmNotes:
      "Beans and Barley Cafe — Seattle WA. Locked out of the portal, the reset email is not arriving, and she wants a bookkeeper added to the account.",
    ordersCount: 1,
    demoScenario: "portal_access",
    orders: [
      { orderId: "PAY-5598120", product: "Weekly draft — $1,962.45", status: "in_transit" },
    ],
  },
  {
    id: "101144",
    name: "Hector Salas",
    firstName: "Hector",
    lastName: "Salas",
    email: "hector.salas@gmail.com",
    phone: "(602) 555-0173",
    loyaltyTier: "Silver",
    crmNotes:
      "Taco Vista Cantina — Phoenix AZ. Under a state sales-tax audit and needs 18 months of invoices, but the portal only holds 15.",
    ordersCount: 2,
    demoScenario: "invoice_history_request",
    orders: [
      { orderId: "INV-8907011", product: "Johnson Brothers — $1,284.60", status: "delivered" },
      { orderId: "INV-8907088", product: "Johnson Brothers — $1,092.75 (archive request)", status: "delivered" },
    ],
  },
];

export const FINTECH_PERSONA_ID_PREFIX = "ftn_";

export function getFintechCustomer(id: string): FintechCustomer | undefined {
  return FINTECH_CUSTOMERS.find((c) => c.id === id);
}

export function fintechCustomerToPersona(c: FintechCustomer): PersonaConfig {
  return {
    id: `${FINTECH_PERSONA_ID_PREFIX}${c.id}`,
    name: c.name,
    role: c.crmNotes || "Client",
    avatarInitials: ((c.firstName[0] ?? "") + (c.lastName[0] ?? "")).toUpperCase(),
    avatarBg: "",
    tagLine: c.crmNotes,
    email: c.email,
    phone: c.phone,
    accountId: `FTN-${c.id}`,
    memberSince: "",
    loyaltyTier: c.loyaltyTier,
    orders: c.orders.map((o): PersonaOrder => ({
      orderId: o.orderId,
      product: o.product,
      status: o.status as OrderStatus,
      date: o.date ?? "",
    })),
    ordersCount: c.ordersCount,
    bestFor: [],
    suggestedQuestions: {},
    crmNotes: c.crmNotes,
  };
}
